import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Formik, Field, Form } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import get from 'lodash/get';
import { FormControl } from '@material-ui/core';
import classnames from 'classnames';
import { loginUser } from '../../containers/App/actions';
import { colors, inputSubmitStyles, fontSize } from '../../constants/styleConstants';
import TextButton from '../TextButton';
import FormikTextField from '../Fields/TextField';
import PasswordField from '../Fields/PasswordField';
import { nextPageQueryName } from '../../utils/auth';
import { TP } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import route from '../../constants/routes';

const useStyles = makeStyles(theme => ({
  infoField: {
    width: '100%',
    position: 'relative',
    marginBottom: 10,
  },
  field: {
    marginBottom: '10px !important',
    width: '100%',
    '& label': {
      color: '#626262 !important',
      [theme.breakpoints.down('xs')]: {
        textTransform: 'uppercase',
      },
      // fontSize: fontSize.textFieldLabel,
    },
    '& >div': {
      marginTop: '18px !important',
      border: 'solid 0.5px rgba(151, 151, 151, 0.5) !important',
      borderRadius: '2px',
      padding: 1,
      '& input': {
        padding: '5px 10px !important',
      },
    },
  },
  checkboxField: {
    flexDirection: 'row !important',
    alignItems: 'center',
    marginBottom: '20px !important',
  },
  checkboxLabel: {
    color: colors.textGray,
    marginLeft: '0.5em',
    fontSize: fontSize.smallTitle,
  },
  loadingBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 200,
  },
  loginButton: {
    ...inputSubmitStyles,
    width: '100%',
  },
  info: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontSize: '11px',
    color: colors.blueLight,
    '& span': {
      fontSize: '11px',
    },
  },
  disable: {
    cursor: 'not-allowed',
  },
}));

const fields = {
  email: {
    id: 'email',
    label: `${TP}.m_EMAIL`,
    type: 'email',
  },
  password: {
    id: 'password',
    label: `${TP}.m_PASSWORD`,
  },
  keepSignedIn: {
    id: 'isTrustedDevice',
    label: `${TP}.KEEP_ME_SIGNED_IN`,
  },
};

const initialFormValues = {
  [fields.password.id]: '',
  [fields.email.id]: '',
  [fields.keepSignedIn.id]: false,
};

const LoginFields = ({ setForgetPassword, url, isLoginLoading }) => {
  const classes = useStyles();
  const Router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const forgetPassword = () => {
    if (setForgetPassword) {
      setForgetPassword(true);
    } else {
      const query = {
        page: 'forgotPassword',
      };
      Router.push({ pathname: route.LOGIN, query });
    }
  };

  const handleAuthError = (err, setFieldError) => {
    if (err.message === 'Password is incorrect') setFieldError(fields.password.id, t(`${TP}.LOGIN_INVALID_PASSWORD`));
    if (err.message === 'User not found') setFieldError(fields.email.id, t(`${TP}.LOGIN_INVALID_EMAIL`));
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={loginValidationSchema}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        const nextPage = url ? url.as : get(Router, `query.${nextPageQueryName}`);
        dispatch(
          loginUser({
            email: values[fields.email.id],
            password: values[fields.password.id],
            isTrustedDevice: values[fields.keepSignedIn.id],
            nextPage,
            onRejected: err => handleAuthError(err, setFieldError),
          }),
        );
        setSubmitting(false);
      }}
      validateOnBlur={false}
    >
      {({ isSubmitting }) => (
        <Form>
          <Field
            className={classes.field}
            key={fields.email.id}
            required
            name={fields.email.id}
            label={t(fields.email.label)}
            type="email"
            component={FormikTextField}
            InputLabelProps={{
              classes: {
                root: classes.inputLabel,
              },
            }}
          />
          <div className={classes.infoField}>
            <PasswordField name={fields.password.id} label={t(fields.password.label)} className={classes.field} />
            <TextButton onClick={() => forgetPassword()} className={classes.info}>
              {t(`${TP}.m_LOGIN_FORGOT_PASSWORD`)}
            </TextButton>
          </div>
          {setForgetPassword && (
            <FormControl className={classes.checkboxField}>
              <Field type="checkbox" name={fields.keepSignedIn.id} id="keep_signd_in" />
              <label htmlFor="keep_signd_in" className={classes.checkboxLabel}>
                {t(fields.keepSignedIn.label)}
              </label>
            </FormControl>
          )}
          <input
            className={classnames(classes.loginButton, isLoginLoading && classes.disable)}
            type="submit"
            disabled={isSubmitting || isLoginLoading}
            value={t(`${TP}.m_LOGIN`)}
          ></input>
        </Form>
      )}
    </Formik>
  );
};

LoginFields.propTypes = {
  setForgetPassword: PropTypes.func,
  isLoginLoading: PropTypes.bool,
  url: PropTypes.object,
};

export default LoginFields;

const loginValidationSchema = () =>
  Yup.object().shape({
    [fields.password.id]: Yup.string('')
      .min(8, `${TP}.ACCOUNT_SETTINGS_FORM_PASSWORD_TOO_SHORT`)
      .required(`${TP}.m_LOGIN_REQUIRED`),
    [fields.email.id]: Yup.string()
      .email(`${TP}.m_MAILNONVALID`)
      .required(`${TP}.m_LOGIN_REQUIRED`),
  });
